import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

/**
 * GlobalErrorHandler - Gestione centralizzata degli errori non gestiti
 * 
 * Cattura errori JavaScript non gestiti e reindirizza alla pagina di login
 * con stato di errore per evitare schermate bianche.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  
  private hasHandledError = false;
  
  constructor(
    private ngZone: NgZone,
    private router: Router
  ) {}

  handleError(error: any): void {
    // Log dell'errore per debugging
    console.error('🔴 GlobalErrorHandler caught error:', error);
    
    // Evita gestione multipla dello stesso errore
    if (this.hasHandledError) {
      return;
    }
    
    // Estrai informazioni sull'errore
    const errorMessage = this.extractErrorMessage(error);
    const errorType = this.classifyError(error);
    
    console.error(`🔴 Error Type: ${errorType}`);
    console.error(`🔴 Error Message: ${errorMessage}`);
    
    // Se siamo già sulla pagina di login, non fare nulla di speciale
    // L'errore sarà gestito dal componente auth stesso
    if (this.isOnAuthPage()) {
      console.log('🔴 Error on auth page - letting component handle it');
      return;
    }
    
    // Per errori critici che bloccano l'app, forza reload alla pagina di login
    if (this.isCriticalError(error)) {
      this.hasHandledError = true;
      console.log('🔴 Critical error detected - forcing reload to login');
      
      // Usa NgZone per eseguire la navigazione nel contesto Angular
      this.ngZone.run(() => {
        // Salva info errore in sessionStorage per mostrarle dopo il reload
        try {
          sessionStorage.setItem('app_error', JSON.stringify({
            title: this.getErrorTitle(errorType),
            message: errorMessage,
            timestamp: new Date().toISOString()
          }));
        } catch (e) {
          console.error('🔴 Failed to save error to sessionStorage:', e);
        }
        
        // Forza reload alla pagina di login
        setTimeout(() => {
          window.location.href = '/#/';
          window.location.reload();
        }, 100);
      });
    }
  }

  /**
   * Estrae il messaggio di errore dall'oggetto error
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.rejection?.message) {
      return error.rejection.message;
    }
    return 'An unexpected error occurred. Please reload the page.';
  }

  /**
   * Classifica il tipo di errore
   */
  private classifyError(error: any): string {
    const message = this.extractErrorMessage(error).toLowerCase();
    
    if (message.includes('network') || message.includes('fetch') || message.includes('http')) {
      return 'network';
    }
    if (message.includes('timeout')) {
      return 'timeout';
    }
    if (message.includes('token') || message.includes('auth') || message.includes('session')) {
      return 'auth';
    }
    if (message.includes('chunk') || message.includes('loading')) {
      return 'loading';
    }
    return 'unknown';
  }

  /**
   * Ottiene il titolo appropriato per il tipo di errore
   */
  private getErrorTitle(errorType: string): string {
    switch (errorType) {
      case 'network':
        return 'Connection Error';
      case 'timeout':
        return 'Request Timeout';
      case 'auth':
        return 'Session Error';
      case 'loading':
        return 'Loading Error';
      default:
        return 'Application Error';
    }
  }

  /**
   * Verifica se siamo sulla pagina di autenticazione
   */
  private isOnAuthPage(): boolean {
    const url = window.location.href;
    return url.endsWith('/') || url.endsWith('/#/') || url.includes('/#/?');
  }

  /**
   * Determina se l'errore è critico e richiede un reload
   */
  private isCriticalError(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    
    // Errori che richiedono reload
    const criticalPatterns = [
      'cannot read properties of null',
      'cannot read properties of undefined',
      'is not a function',
      'chunk failed',
      'loading chunk',
      'dynamically imported module',
      'failed to fetch',
      'network error'
    ];
    
    return criticalPatterns.some(pattern => message.includes(pattern));
  }
}
